import type { ChangeEvent } from 'react'

import Checkbox from '@mui/material/Checkbox'
import TableCell from '@mui/material/TableCell'
import selectedCheckboxIcon from 'assets/image/icons/tables/checkbox2.svg'
import partiallySelectedCheckboxIcon from 'assets/image/icons/tables/checkbox3.svg'
import notSelectedCheckboxIcon from 'assets/image/icons/tables/checkbox.svg'
import ExportToCSVButton from 'components/atoms/ExportToCSVButton'
import { campaignApi } from 'features/campaigns'
import { useRequestParams } from 'features/campaigns/hooks/request-params'
import * as files from 'shared/files'

type WithId = {
	id: string
}

export function SelectHeader<I extends WithId>({
	areAllSelected,
	campaigns,
	onChangeCheckbox,
	selectAll,
	selected,
	total,
	unSelectAll
}: {
	areAllSelected: boolean
	campaigns: I[]
	onChangeCheckbox?: (
		event: ChangeEvent<HTMLInputElement>,
		checked: boolean
	) => void
	selectAll(): void
	selected: Set<string>
	total: number
	unSelectAll(): void
}) {
	const [params] = useRequestParams()
	const [getCSV, { isLoading }] = campaignApi.useExportCampaignMutation()

	async function saveData() {
		if (!areAllSelected) {
			params.$filter = `id in (${Array.from(selected, id => `'${id}'`)})`
			params.$searchQuery = undefined
		}

		const { data: content } = await getCSV(params)

		if (content !== undefined) {
			files.saveFile(content, files.generateName())

			unSelectAll()
		}
	}

	const hasRows = campaigns.length > 0

	const isChecked =
		areAllSelected ||
		(hasRows &&
			selected.size >= campaigns.length &&
			campaigns.every(campaign => selected.has(campaign.id)))

	const isIntermediate = Boolean(!areAllSelected && selected.size && !isChecked)
	const amount = areAllSelected ? total : selected.size

	return (
		<>
			<TableCell style={{ width: '5%' }}>
				<div className='checkbox-container-header'>
					<Checkbox
						checked={isChecked && !isIntermediate}
						checkedIcon={<img alt='checkbox' src={selectedCheckboxIcon} />}
						disabled={!hasRows}
						icon={<img alt='checkbox' src={notSelectedCheckboxIcon} />}
						indeterminate={isIntermediate}
						indeterminateIcon={
							<img alt='checkbox' src={partiallySelectedCheckboxIcon} />
						}
						onChange={onChangeCheckbox}
						slotProps={{
							input: {
								'aria-label': 'select all desserts'
							}
						}}
					/>
				</div>
			</TableCell>
			<TableCell className='name title-company' style={{ width: '28%' }}>
				{selected.size > 0 ? (
					<div className='selected-rows'>
						{amount > 0 && (
							<div>
								<span className='select-text'>
									{amount} of {total} selected |{' '}
								</span>

								{areAllSelected ? (
									<span className='clear-all' onClick={unSelectAll}>
										Clear
									</span>
								) : (
									<span className='clear-all' onClick={selectAll}>
										Select all
									</span>
								)}
							</div>
						)}
						<div className='background-button'>
							<ExportToCSVButton
								disabled={false}
								isLoading={isLoading}
								onClick={saveData}
								text='Export to CSV'
							/>
						</div>
					</div>
				) : (
					'Campaign'
				)}
			</TableCell>
		</>
	)
}
